import React from 'react';
import {
  Box,
  TextField,
  Grid,
  Typography,
  Divider
} from '@mui/material';
import type { Person, Location } from '../../../../types';

interface LocationsFormProps {
  data: Partial<Person>;
  errors: Record<string, string>;
  onChange: (field: string, value: any) => void;
  disabled?: boolean;
}

/**
 * LocationsForm - Edit location information
 *
 * Fields:
 * - Birth Place (city, state, country)
 * - Current Location (city, state, country)
 * - Death Place (city, state, country) - disabled if not deceased
 */
export const LocationsForm: React.FC<LocationsFormProps> = ({
  data,
  errors,
  onChange,
  disabled = false
}) => {
  const handleLocationChange = (locationType: 'birthPlace' | 'currentLocation' | 'deathPlace', field: keyof Location, value: string) => {
    const currentLocation = data[locationType] || { city: '', state: '', country: '' };
    onChange(locationType, {
      ...currentLocation,
      [field]: value
    });
  };

  const birthPlace = data.birthPlace || { city: '', state: '', country: '' };
  const currentLocation = data.currentLocation || { city: '', state: '', country: '' };
  const deathPlace = data.deathPlace || { city: '', state: '', country: '' };

  return (
    <Box sx={{ pt: 2 }}>
      <Grid container spacing={3}>
        {/* Birth Place Section */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Birth Place
          </Typography>
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="City"
            value={birthPlace.city || ''}
            onChange={(e) => handleLocationChange('birthPlace', 'city', e.target.value)}
            disabled={disabled}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="State/Province"
            value={birthPlace.state || ''}
            onChange={(e) => handleLocationChange('birthPlace', 'state', e.target.value)}
            disabled={disabled}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Country"
            value={birthPlace.country || ''}
            onChange={(e) => handleLocationChange('birthPlace', 'country', e.target.value)}
            disabled={disabled}
          />
        </Grid>

        {/* Divider */}
        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Current Location Section */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Current Location
          </Typography>
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="City"
            value={currentLocation.city || ''}
            onChange={(e) => handleLocationChange('currentLocation', 'city', e.target.value)}
            disabled={disabled}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="State/Province"
            value={currentLocation.state || ''}
            onChange={(e) => handleLocationChange('currentLocation', 'state', e.target.value)}
            disabled={disabled}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Country"
            value={currentLocation.country || ''}
            onChange={(e) => handleLocationChange('currentLocation', 'country', e.target.value)}
            disabled={disabled}
          />
        </Grid>

        {/* Divider */}
        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Death Place Section */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Death Place
          </Typography>
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="City"
            value={deathPlace.city || ''}
            onChange={(e) => handleLocationChange('deathPlace', 'city', e.target.value)}
            disabled={disabled || !data.isDeceased}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="State/Province"
            value={deathPlace.state || ''}
            onChange={(e) => handleLocationChange('deathPlace', 'state', e.target.value)}
            disabled={disabled || !data.isDeceased}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Country"
            value={deathPlace.country || ''}
            onChange={(e) => handleLocationChange('deathPlace', 'country', e.target.value)}
            disabled={disabled || !data.isDeceased}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
